/**
 * ExpressionControlErrorBoundary — keeps a failed lazy chunk from destroying the whole `expression`
 * field.
 *
 * ── The bug this exists to fix ─────────────────────────────────────────────────────────────────
 *
 * `ExpressionControl` reaches `@bizfirst/expressions-designer` through `React.lazy`, and `Suspense`
 * catches only the *pending* state of that import. A **rejected** import promise is thrown during
 * render like any other error: it unwinds past `ExpressionControl` entirely, past `FormField`'s
 * `default:` branch, and is caught by `FormRenderer`'s `FieldErrorBoundary`, which replaces the
 * whole field with a generic "Field error". That bypassed this control's own careful unconfigured-
 * host messaging and showed a raw error string in its place.
 *
 * With this boundary, a chunk that will not load produces the same explanatory notice the
 * unconfigured-host branch produces — including the raw current value, so a stored expression is
 * never silently hidden by a transport failure.
 *
 * ── Why it is deliberately narrow ──────────────────────────────────────────────────────────────
 *
 * It wraps *only* the lazy preview + launcher. It is not a general-purpose boundary and must not
 * become one: swallowing render errors from the field itself is exactly what `FieldErrorBoundary`
 * exists to surface.
 *
 * `@atlas-forms/player-components-react` has its own copy of this component for §3B/§3C's trigger.
 * The two packages are siblings with no dependency between them — the same deliberate split Plan/05
 * §3B makes for `configureExpressionBuilder`/`configureExpressionControl`.
 */

import React from 'react';
import { isDevBuild } from './expressionControlRuntime';

export interface ExpressionControlErrorBoundaryProps {
  /** Rendered in place of the children once a lazy import has rejected. */
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface ExpressionControlErrorBoundaryState {
  failed: boolean;
}

export class ExpressionControlErrorBoundary extends React.Component<
  ExpressionControlErrorBoundaryProps,
  ExpressionControlErrorBoundaryState
> {
  state: ExpressionControlErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ExpressionControlErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    // Not silent: a chunk that never loads is a real deployment problem worth seeing in a log, even
    // though the user-visible consequence is a notice rather than a crash.
    //
    // `isDevBuild()` rather than an inline `NODE_ENV` check: the inline `(globalThis as any)
    // .process?.env?.NODE_ENV` form this used to carry contains no token a bundler substitutes and
    // evaluates to `true` in every browser build, so this "dev-only" warning also fired in
    // production. See `isDevBuild` for the full explanation.
    if (isDevBuild()) {
      console.warn(
        '[ExpressionControl] The Expression Builder could not be loaded; the field is showing its ' +
          'stored value read-only instead.',
        error,
      );
    }
  }

  render(): React.ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export default ExpressionControlErrorBoundary;
